import { AirportModel } from 'app/airport/models/airport.model';
import { classifyField, classifySection, displayText } from './field-presence';
import {
  V2BriefingChip,
  V2BriefingContext,
  V2BriefingField,
  V2BriefingFieldDef,
  V2BriefingGateDivergence,
  V2BriefingSection,
  V2BriefingSectionDef,
  V2BriefingSectionId,
} from './v2-briefing.model';

/**
 * The six sections of the v2 briefing, as data.
 *
 * The UI iterates this rather than carrying six hardcoded templates, so
 * adding a field is a line here and moving one between sections is a line
 * here. The mapping is the briefing README's Structure table, extended to
 * absorb *every* field the current 292-line
 * `airport-details.component.html` renders -- the README's table quietly
 * omits `latitude`, `longitude`, `time_zone` and the handler chart files,
 * all of which are on screen today.
 *
 * Nothing in this file parses, derives or infers. Field keys are property
 * names on the record; labels are the current briefing's own wording,
 * sentence-cased for v2.
 */

const OVERVIEW: V2BriefingSectionDef = {
  id: 'overview',
  label: 'Overview',
  gated: false,
  fields: [
    { key: 'latitude', label: 'Latitude', source: 'airport', kind: 'text', v1Gated: false },
    { key: 'longitude', label: 'Longitude', source: 'airport', kind: 'text', v1Gated: false },
    { key: 'elevation', label: 'Airport elevation', source: 'airport', kind: 'text', v1Gated: false },
    // `magnetic` is left out until the stored values are refreshed: a stale
    // variation reads as a current one.
    { key: 'approaches', label: 'Approaches', source: 'airport', kind: 'rich-text', v1Gated: false },
    // Gated in v1, ungated here because Overview is one of the two sections a
    // no-access viewer reads in full. Reported by briefingGateDivergences().
    { key: 'alternates', label: 'Closest alternates', source: 'airport', kind: 'text', v1Gated: true },
    { key: 'to_downtown', label: 'Direction and distance from city', source: 'airport', kind: 'rich-text', v1Gated: false },
    { key: 'usage_type', label: 'Airport usage type', source: 'airport', kind: 'text', v1Gated: false },
    { key: 'time_zone', label: 'Time zone', source: 'airport', kind: 'rich-text', v1Gated: false },
  ],
};

const RUNWAYS: V2BriefingSectionDef = {
  id: 'runways',
  label: 'Runways',
  gated: false,
  fields: [
    // data-gaps G2: one free-text field, rendered as stored. No parser, no
    // per-runway cards, no split into length / LDA / surface / PCN cells.
    { key: 'rwy_lda_pcn', label: 'RWY(s), size (LDA), surface, PCN', source: 'airport', kind: 'text', v1Gated: false },
    { key: 'airport_additionals', label: 'Additional runway information', source: 'airport', kind: 'additionals', v1Gated: false },
    { key: 'fire_cat', label: 'RFFS rating', source: 'airport', kind: 'rich-text', v1Gated: false },
  ],
};

const NOISE: V2BriefingSectionDef = {
  id: 'noise',
  label: 'Noise',
  gated: true,
  fields: [
    { key: 'noise', label: 'Noise regulations', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'restrictions', label: 'Restrictions', source: 'airport', kind: 'rich-text', v1Gated: true },
    // Ungated in v1. Putting it in a gated section withholds from a
    // no-access viewer something they can read today -- the one narrowing in
    // the whole map, and the reason briefingGateDivergences() exists.
    { key: 'slots', label: 'Slots required', source: 'airport', kind: 'rich-text', v1Gated: false },
    { key: 'ops_hours', label: 'Operational hours', source: 'airport', kind: 'rich-text', v1Gated: true },
  ],
};

const OPS: V2BriefingSectionDef = {
  id: 'ops',
  label: 'Ops & handling',
  gated: true,
  fields: [
    { key: 'name', label: 'Handler', source: 'handler', kind: 'text', v1Gated: true },
    { key: 'address', label: 'Address', source: 'handler', kind: 'text', v1Gated: true },
    { key: 'handler_info_phones', altKey: 'handlerInfoPhones', label: 'Tel', source: 'handler', kind: 'phones', v1Gated: true },
    { key: 'fax', label: 'Fax', source: 'handler', kind: 'text', v1Gated: true },
    { key: 'handler_info_email', altKey: 'handlerInfoEmails', label: 'Email', source: 'handler', kind: 'emails', v1Gated: true },
    { key: 'web', label: 'Web', source: 'handler', kind: 'url', v1Gated: true },
    { key: 'vhf', label: 'VHF', source: 'handler', kind: 'text', v1Gated: true },
    { key: 'name_ipa', label: 'Into-plane agent name', source: 'handler', kind: 'text', v1Gated: true },
    { key: 'fuel_truck', label: 'Fuel truck availability', source: 'handler', kind: 'text', v1Gated: true },
    { key: 'add_info', label: 'Additional fuel information', source: 'handler', kind: 'rich-text', v1Gated: true },
    { key: 'parking', label: 'Parking', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'power_parking', label: 'Power in / out parking', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'hangar', label: 'Hangar', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'meet_point', label: 'Meeting point for PAX and crew', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'catering', label: 'Catering', source: 'airport', kind: 'rich-text', v1Gated: true },
    // Fees had a tab of their own; two rows did not earn one.
    { key: 'airport_fees', label: 'Airport fees', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'misc', label: 'Miscellaneous', source: 'airport', kind: 'rich-text', v1Gated: true },
  ],
};

const ENTRY: V2BriefingSectionDef = {
  id: 'entry',
  label: 'Entry & visas',
  gated: true,
  fields: [
    { key: 'entry_airport', label: 'Airport of entry', source: 'airport', kind: 'boolean', v1Gated: true },
    { key: 'permits', label: 'Permits', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'visas', label: 'Visa requirements', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'ciq', label: 'CIQ procedures', source: 'airport', kind: 'rich-text', v1Gated: true },
    { key: 'pilot_age', label: 'Pilot age restrictions', source: 'airport', kind: 'rich-text', v1Gated: true },
  ],
};

const CHARTS: V2BriefingSectionDef = {
  id: 'charts',
  label: 'Charts',
  gated: true,
  fields: [
    { key: 'airport_files', label: 'Maps & charts', source: 'airport', kind: 'files', v1Gated: true },
    // `mapFiles` in the old component concatenates the airport's files with
    // the selected handler's, under whichever of the two spellings arrived.
    { key: 'handler_files', altKey: 'handlerInfoFiles', label: 'Handler charts', source: 'handler', kind: 'files', v1Gated: true },
  ],
};

export const V2_BRIEFING_SECTIONS: readonly V2BriefingSectionDef[] = [
  OVERVIEW, RUNWAYS, NOISE, OPS, ENTRY, CHARTS,
];

/**
 * The two sections that stay readable for a viewer without `rights_briefing`
 * -- spec §6.2. Everything else is blurred behind the veil.
 */
export const V2_UNGATED_SECTION_IDS: readonly V2BriefingSectionId[] =
  V2_BRIEFING_SECTIONS.filter(section => !section.gated).map(section => section.id);

export function briefingSectionDef(id: V2BriefingSectionId): V2BriefingSectionDef | undefined {
  return V2_BRIEFING_SECTIONS.find(section => section.id === id);
}

export function isGatedSection(id: V2BriefingSectionId): boolean {
  return !!briefingSectionDef(id)?.gated;
}

/**
 * Where v2's section gating disagrees with v1's row gating.
 *
 * v1 gates individual rows inside one table; v2 gates whole sections. Sorting
 * the same fields into six sections therefore has to move a few of them
 * across the wall, and "a no-access viewer loses a field they can read today"
 * is not a thing to discover in production. The spec asserts the exact list,
 * so moving a field between sections fails a test rather than a briefing.
 *
 * As of 2026-09-04 these are enacted rather than cosmetic. The server
 * withholds the gated fields outright -- see `UNGATED_BRIEFING_FIELDS` in
 * backend/evo_estimator/airports/briefing_gate.py, which is authoritative and
 * which this map must agree with. So the two entries below are the actual
 * entitlement changes a release note has to mention: `slots` withdrawn,
 * `alternates` granted. data-gaps.md G16/G17.
 */
export function briefingGateDivergences(): readonly V2BriefingGateDivergence[] {
  const divergences: V2BriefingGateDivergence[] = [];

  for (const section of V2_BRIEFING_SECTIONS) {
    for (const field of section.fields) {
      if (field.v1Gated === section.gated) { continue; }
      divergences.push({
        key: field.key,
        section: section.id,
        v1Gated: field.v1Gated,
        v2Gated: section.gated,
        direction: section.gated ? 'narrowed' : 'widened',
      });
    }
  }

  return divergences;
}

function sourceObject(def: V2BriefingFieldDef, context: V2BriefingContext): Record<string, unknown> | null {
  const source = def.source === 'handler' ? context.handler : context.airport;
  return (source as unknown as Record<string, unknown>) ?? null;
}

/** Read one field off the record and say which of the three states it is in. */
export function resolveBriefingField(
  def: V2BriefingFieldDef,
  context: V2BriefingContext,
): V2BriefingField {
  const source = sourceObject(def, context);

  let value = source ? source[def.key] : undefined;
  if ((value === undefined || value === null) && def.altKey && source) {
    value = source[def.altKey];
  }

  return {
    def,
    value,
    text: displayText(value),
    presence: classifyField(value),
  };
}

export function buildBriefingSection(
  def: V2BriefingSectionDef,
  context: V2BriefingContext,
): V2BriefingSection {
  const fields = def.fields.map(field => resolveBriefingField(field, context));

  return {
    def,
    fields,
    presence: classifySection(fields.map(field => field.presence)),
    locked: def.gated && !context.canReadDetail,
  };
}

/**
 * All six, always. A locked section is still returned so the switcher can
 * show it with a padlock -- a section that vanished would read as an airport
 * with no fees rather than an account without access.
 */
export function buildBriefingSections(context: V2BriefingContext): readonly V2BriefingSection[] {
  return V2_BRIEFING_SECTIONS.map(section => buildBriefingSection(section, context));
}

/**
 * The hero chips (data-gaps G3).
 *
 * One chip, from the one real boolean, and only when it is true.
 * `entry_airport` is `BooleanField(default=False)` on the Django model, so
 * `false` is also what an untouched record holds: it is the absence of a
 * claim, not the claim that this is not an airport of entry. The row inside
 * Entry & visas still renders it either way; the hero does not.
 *
 * Nothing is pattern-matched out of `ops_hours`, `fire_cat`, `noise` or
 * `slots`. A chip is a claim read at a glance, and a confident wrong chip on
 * a briefing is a safety problem rather than a cosmetic one.
 */
export function briefingHeroChips(airport: AirportModel | null | undefined): readonly V2BriefingChip[] {
  // Read through `unknown`: `AirportModel` types this column as the *literal*
  // `false`, so a direct `=== true` is a compile error against a model that
  // does not describe the payload. Same generated-model artefact as the
  // dozen columns typed as the literal `null`.
  const entryAirport = (airport as unknown as { entry_airport?: boolean } | null | undefined)?.entry_airport;

  return entryAirport === true
    ? [{ key: 'entry_airport', label: 'Airport of entry' }]
    : [];
}
